import { useEffect, useRef } from 'react';
import { ActionMenu, Button, useConfirm } from '@beyond-js/ui/react';
import { Condition, Policy, Describe, type Capture, type Use } from '@snapshots/domain';
import { Copies, useFrame } from '@snapshots/navigation';
import { usePresence } from '@snapshots/session';
import { Problems } from '@snapshots/ui';

interface Specs {
	capture: Capture;
	busy: boolean;
	/** Runs a change that answers the snapshot as it is now */
	/** `again`: the answer offers its own Try again, for a change whose control is not beside it */
	act: (work: () => Promise<Capture>, outcome: (capture: Capture) => string, again?: boolean) => Promise<void>;
	/** Shows the snapshot as a confirmed change left it, with its outcome */
	settle: (capture: Capture | undefined, outcome?: string) => void;
}

/**
 * Cancelling, deleting and restoring a snapshot, at the end of its header's title line (D52). Restoring a
 * deleted one is an administrator's one primary action. Everything else is in "More actions for {title}":
 * **Cancel** a pending snapshot, for whoever started it or an owner or admin (decision E9), and **Delete** a
 * completed one, for an owner or admin (PX-02), in the danger tone. A person whose role does not allow an
 * item sees it disabled with the reason, where they reach for it (SNP-17), and no card about actions they
 * never tried. Both confirm with their own verb and say their consequence first (D17): deleting a snapshot a
 * release holds names those releases and that they can no longer be promoted or rolled back to it (SNP-03).
 */
export function Lifecycle({ capture, busy, act, settle }: Specs) {
	const { text, identity, captures, date, language, short } = usePresence();
	const questions = useConfirm(Copies.questions(language));
	const { renewal } = useFrame();
	// Restore and the More menu take each other's place, so focus follows to the control that is there now
	const holder = useRef<HTMLSpanElement>(null);
	const moved = useRef(false);
	useEffect(() => {
		if (!moved.current) return;
		moved.current = false;
		holder.current?.querySelector('button')?.focus();
	}, [capture.state]);

	const standing = Condition.of(capture);
	const title = Describe.title(capture, text, short);
	const explain = (cause: unknown) => {
		const refusal = Problems.refusal(cause);
		// A change is never sent again after a renewal: the person presses it again, told so beside it
		if (refusal.quiet && renewal) return renewal.text('unsent');
		// An outage is said in the dialog the family's way (D186): nothing was changed, or it may have been, and
		// the snapshot is read again once the dialog is left; a refusal is the dialog's own error
		return Problems.outcome(text, refusal, language) ?? Problems.say(text, refusal);
	};
	const until = (days: number | undefined) => (days ? date(new Date(Date.now() + days * 86_400_000).toISOString()) : undefined);
	const releases = (uses: Use[]) => uses.filter(use => use.reference.startsWith('release.')).map(use => use.label ?? text('capture.use.release', { product: 'CDN' }));

	const cancel = async () => {
		let ended: Capture | undefined;
		const kept = until((await captures.facts()).ended);
		const confirmed = await questions.confirm({
			title: text('capture.cancel.question', { title }),
			message: text('capture.cancel.message'),
			consequence: { lost: text('capture.cancel.lost'), kept: kept ? text('capture.cancel.kept', { until: kept }) : text('capture.cancel.kept.period'), recovery: text('capture.cancel.recovery') },
			accept: text('capture.cancel.confirm'),
			cancel: text('capture.cancel.keep'),
			tone: 'danger',
			work: async () => (ended = await captures.cancel(capture.id)),
			explain
		});
		moved.current = Boolean(confirmed && ended);
		settle(confirmed ? ended : undefined, confirmed && ended ? text('capture.cancelled.outcome') : undefined);
	};
	const remove = async () => {
		let removed: Capture | undefined;
		const recoverable = until(await captures.recovery());
		const held = releases(capture.uses ?? []);
		const holds = held.length > 0 || Boolean(capture.references);
		const confirmed = await questions.confirm({
			title: text('capture.delete.question', { title }),
			message: text('capture.delete.message'),
			consequence: {
				lost: holds ? text('capture.delete.lost.held', { releases: held.length ? held.join(', ') : text('capture.use.release', { product: 'CDN' }) }) : text('capture.delete.lost'),
				kept: recoverable ? text('capture.delete.kept', { until: recoverable }) : text('capture.delete.kept.period'),
				recovery: text(holds ? 'capture.delete.recovery.held' : 'capture.delete.recovery')
			},
			accept: text('capture.delete.confirm'),
			cancel: text('capture.delete.keep'),
			tone: 'danger',
			work: async () => (removed = await captures.delete(capture.id)),
			explain
		});
		// A deletion that got no answer may have happened: the snapshot is read again either way
		moved.current = Boolean(confirmed && removed);
		settle(confirmed ? removed : undefined, confirmed && removed ? text('capture.deleted.outcome', { until: date(removed.recoverable) }) : undefined);
	};
	const restore = () => {
		moved.current = true;
		// Its answer is said in the page, apart from Restore in the header: Try again goes with it
		void act(() => captures.restore(capture.id), () => text('capture.restored.outcome'), true);
	};

	if (standing === 'deleted') {
		if (!Policy.deletes(identity, capture.organization)) return null;
		return (
			<span ref={holder} data-lifecycle={capture.state}>
				<Button variant="primary" label={text('action.restore')} disabled={busy} onClick={restore} />
			</span>
		);
	}
	const items =
		standing === 'pending'
			? [{ label: text('capture.cancel.item', { title }), tone: 'danger' as const, disabled: busy || !Policy.cancels(identity, capture), reason: Policy.cancels(identity, capture) ? null : text('reason.cancel'), onSelect: () => void cancel() }]
			: standing === 'completed'
				? [{ label: text('capture.delete.item', { title }), tone: 'danger' as const, disabled: busy || !Policy.deletes(identity, capture.organization), reason: Policy.deletes(identity, capture.organization) ? null : text('reason.delete'), onSelect: () => void remove() }]
				: [];
	if (!items.length) return null;
	return (
		<span ref={holder} data-lifecycle={capture.state}>
			<ActionMenu name={text('capture.more', { title })} items={items} />
		</span>
	);
}
